import { cn } from '@rocco/utils/cn';
import { Image } from '@rocco/components/image';
import type { MagazineDirectoryTileViewModel } from '~/modules/magazine/type';

type MagazineType =
    | 'Dedicated EDM'
    | 'ArchiPros Talk'
    | 'Build'
    | 'The Project Portfolio'
    | 'Curated Collection'
    | 'Commercial Edit'
    | 'Project Feature'
    | 'Misc';

/**
 * Ground and ink for a composed cover, by series.
 */
export const getMagazineCoverColours = (type: string): string => {
    switch (type as MagazineType) {
        case 'ArchiPros Talk':
            return 'bg-[#8E8580] text-[#E5E5E5]';
        case 'Commercial Edit':
            return 'bg-[#D4D1CC] text-[#343434]';
        case 'Curated Collection':
            return 'bg-[#000000] text-[#E5E5E5]';
        // Build, Dedicated EDM, Project Feature, The Project Portfolio and Misc
        // all share this ground.
        default:
            return 'bg-[#989485] text-[#E5E5E5]';
    }
};

interface MagazineCoverProps {
    magazine: Pick<
        MagazineDirectoryTileViewModel,
        | 'imageSrc'
        | 'title'
        | 'subtitle'
        | 'formattedTitleHTML'
        | 'type'
        | 'frameStyle'
    >;
    className?: string;
}

/**
 * A magazine cover as the magazines directory draws it.
 *
 * Two renderings, which is the part worth knowing: a `Frameless` issue is a
 * finished artwork and shows as-is, while a framed one is *composed* here —
 * series ground, subtitle, masthead and the photograph beneath. Treating a
 * framed issue as a single image crops the masthead off, because the banner
 * photo is only the lower portion of the cover.
 *
 * Extracted from MagazineCard so surfaces outside the magazines directory can
 * show a cover without reproducing that rule.
 */
export const MagazineCover = ({ magazine, className }: MagazineCoverProps) => {
    const { imageSrc, title, subtitle, formattedTitleHTML, type, frameStyle } =
        magazine;

    if (frameStyle === 'Frameless') {
        /*
         * `fit` defaults to no object-fit class, which leaves the CSS initial
         * value `fill` — the banner is stretched to the tile box rather than
         * cropped to it. Banner uploads have no enforced aspect, so `cover` is
         * what keeps them undistorted.
         */
        return (
            <Image
                className={cn('size-full', className)}
                src={imageSrc}
                alt={title}
                fit="cover"
            />
        );
    }

    return (
        <div
            className={cn(
                'flex size-full flex-col p-2 md:p-4',
                getMagazineCoverColours(type),
                className,
            )}
        >
            <div
                className="mb-1.5 line-clamp-2 min-h-9 text-4.5 leading-none md:mb-8 md:min-h-18 md:text-9"
                dangerouslySetInnerHTML={{ __html: subtitle }}
            />
            <div
                className="mb-2 min-h-13 text-6 leading-[1.1] tracking-[-0.02em] md:mb-4 md:min-h-24 md:text-12"
                dangerouslySetInnerHTML={{ __html: formattedTitleHTML }}
            />
            <Image
                className="min-h-0 flex-1"
                src={imageSrc}
                alt={title}
                fit="cover"
            />
        </div>
    );
};
